import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button } from '../../../atoms'

declare var window: interfaces.IWindow

interface IAgentLoginProps {
  alert?: string
  googleAuthUrl: string
}

const AgentLogin: React.FC<IAgentLoginProps> = ({ alert, googleAuthUrl }) => {
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState(alert || '')
  const [loading, setLoading] = React.useState(false)

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError('')
      setLoading(true)

      try {
        const response = await fetch('/login/agent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            agent_member: { email, password },
          }),
        })

        if (response.ok) {
          window.location.href = '/agent/dashboard'
        } else {
          const data = await response.json()
          setError(data.errors || I18n.t('session.invalid_mail_or_password'))
        }
      } catch {
        setError(I18n.t('session.invalid_mail_or_password'))
      } finally {
        setLoading(false)
      }
    },
    [email, password, csrfToken]
  )

  return (
    <Wrapper>
      <LoginBox>
        <h2>Agent Member Login</h2>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <form onSubmit={handleSubmit}>
          <InputField>
            <label>{I18n.t('generic.email')}</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required={true}
              autoComplete="email"
            />
          </InputField>
          <InputField>
            <label>{I18n.t('generic.password')}</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required={true}
              autoComplete="current-password"
            />
          </InputField>
          <Button primary={true} disabled={loading}>
            {loading ? '...' : I18n.t('generic.login')}
          </Button>
        </form>
        <Divider>
          <span>OR</span>
        </Divider>
        <GoogleSSOButton href={googleAuthUrl}>
          <img src="/images/logo_google.svg" alt="" />
          <span>Sign in with Google</span>
        </GoogleSSOButton>
      </LoginBox>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - ${constants.HEADER_HEIGHT + constants.FOOTER_HEIGHT}px);
  background-color: ${constants.COLORS.background};
`

const LoginBox = styled.div`
  width: 100%;
  max-width: 400px;
  padding: 40px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  h2 {
    text-align: center;
    margin-bottom: 24px;
    font-size: 20px;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .Button[type='submit'] {
    width: 100%;
    margin-top: 8px;
  }
`

const InputField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 14px;
    font-weight: 600;
  }

  input {
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      border-color: ${constants.COLORS.primary};
      outline: none;
    }
  }
`

const ErrorMessage = styled.div`
  color: #e74c3c;
  background: #fdeaeb;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ccc;
  }

  span {
    padding: 0 12px;
    color: #999;
    font-size: 13px;
  }
`

const GoogleSSOButton = styled.a`
  display: flex;
  align-items: center;
  height: 50px;
  background-color: ${constants.COLORS.white};
  box-shadow: 0 1px 3px 0 rgba(21, 27, 38, 0.15);
  border-radius: 4px;
  text-decoration: none;
  overflow: hidden;
  color: ${constants.COLORS.black};
  font-size: 16px;
  transition: all 0.1s;

  &:hover {
    box-shadow: 0 1px 3px 2px rgba(21, 27, 38, 0.15);
    background-color: #f5f5f5;
  }

  > img {
    height: inherit;
  }

  > span {
    margin-left: 12px;
  }
`

export default AgentLogin
